<template>
    <div class="cluster-detail-cell">
        <!-- 汇总行 -->
        <div class="summary-row">
            <div class="summary-item">
                <div class="icon-box">
                    <img src="@/assets/images/station-monitor/detail-dialog/cell-num.png" alt="" />
                </div>
                <div class="info">
                    <div class="label">电芯数量</div>
                    <div class="value">{{ cellCount }}</div>
                </div>
            </div>
            <div class="divider"></div>
            <div class="summary-item">
                <div class="icon-box">
                    <img :src="tempImg" alt="" />
                </div>
                <div class="info">
                    <div class="label">温度</div>
                    <div class="value-group">
                        <span>{{ fmtd(census.minECT, 1, 1) }}℃ <img :src="arrowDownIcon" class="arrow-icon" alt="最小值" /></span>
                        <span>{{ fmtd(census.avgECT, 1, 1) }}℃</span>
                        <span>{{ fmtd(census.maxECT, 1, 1) }}℃ <img :src="arrowUpIcon" class="arrow-icon" alt="最大值" /></span>
                    </div>
                </div>
            </div>
            <div class="divider"></div>
            <div class="summary-item">
                <div class="icon-box">
                    <img src="@/assets/images/station-monitor/detail-dialog/voltage.png" alt="" />
                </div>
                <div class="info">
                    <div class="label">电压</div>
                    <div class="value-group">
                        <span>{{ fmtd(census.minECU, 1, 1) }}mV <img :src="arrowDownIcon" class="arrow-icon" alt="最小值" /></span>
                        <span>{{ fmtd(census.avgECU, 1, 1) }}mV</span>
                        <span>{{ fmtd(census.maxECU, 1, 1) }}mV <img :src="arrowUpIcon" class="arrow-icon" alt="最大值" /></span>
                    </div>
                </div>
            </div>
            <div class="divider"></div>
            <div class="summary-item">
                <div class="icon-box">
                    <img src="@/assets/images/station-monitor/detail-dialog/resistance.png" alt="" />
                </div>
                <div class="info">
                    <div class="label">内阻</div>
                    <div class="value-group">
                        <span>{{ fmtd(census.minECR, 1, 2) }}mΩ <img :src="arrowDownIcon" class="arrow-icon" alt="最小值" /></span>
                        <span>{{ fmtd(census.avgECR, 1, 2) }}mΩ</span>
                        <span>{{ fmtd(census.maxECR, 1, 2) }}mΩ <img :src="arrowUpIcon" class="arrow-icon" alt="最大值" /></span>
                    </div>
                </div>
            </div>
        </div>

        <div class="main-content">
            <!-- 左侧 Pack 选择 -->
            <div class="left-panel">
                <div class="pack-list-wrapper">
                    <div class="pack-grid" :class="{ 'single-col': isSingle }">
                        <div v-for="i in packSize" :key="i" class="pack-item" :class="{ active: curPackIdx === i }"
                            @click="curPackIdx = i">
                            <span class="pack-num">{{ String(i).padStart(2, '0') }}</span>
                        </div>
                    </div>
                </div>
                <div class="legend">
                    <div class="legend-item">
                        <span class="dot normal"></span>
                        <span>正常电芯</span>
                    </div>
                    <div class="legend-item">
                        <span class="dot low"></span>
                        <span>低温电芯</span>
                    </div>
                    <div class="legend-item">
                        <span class="dot high"></span>
                        <span>高温电芯</span>
                    </div>
                </div>
            </div>

            <!-- 右侧电芯列表 -->
            <div class="right-panel">
                <div class="cell-grid">
                    <div v-for="(cell, index) in curBmu.cells" :key="index" class="cell-card">
                        <div class="cell-header">
                            <span class="cell-index">{{ cell.number }}#电芯</span>
                            <span class="pack-ver">V{{ cell.packNumber || '01' }}</span>
                        </div>
                        <div class="cell-body">
                            <div class="cell-img">
                                <!-- 根据状态显示不同颜色的电池图标 -->
                                <img :src="getCellImage(cell)" alt="battery" />
                            </div>
                            <div class="cell-info">
                                <div class="info-row">
                                    <span class="label">电芯温度</span>
                                    <span class="value">{{ fmtd(cell.T, 1, 1) }} ℃</span>
                                </div>
                                <div class="info-row">
                                    <span class="label">电芯电压</span>
                                    <span class="value">{{ fmtd(cell.U, 1, 1) }} mV</span>
                                </div>
                                <div class="info-row">
                                    <span class="label">电芯内阻</span>
                                    <span class="value">{{ fmtd(cell.R, 1, 2) }} mΩ</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { fmtd } from '@/utils/index'
import cellImg from '@/assets/images/station-monitor/detail-dialog/cell.png'
import cellLowImg from '@/assets/images/station-monitor/detail-dialog/cell-low.png'
import cellHighImg from '@/assets/images/station-monitor/detail-dialog/cell-high.png'
// 注意文件名有空格
import tempImg from '@/assets/images/station-monitor/detail-dialog/ temperature.png'
import packImg from '@/assets/images/station-monitor/detail-dialog/battery.png'
import packSelectImg from '@/assets/images/station-monitor/detail-dialog/battery-select.png'
import arrowDownIcon from '@/assets/icons/svg/arrow-down.svg'
import arrowUpIcon from '@/assets/icons/svg/arrow-up.svg'

const props = defineProps({
    batteryData: {
        type: Object,
        default: () => ({})
    }
})

// 统计数据
const census = computed(() => {
    const obj = {}
    try {
        if (props.batteryData.census) {
            Object.keys(props.batteryData.census).forEach((key) => {
                const val = props.batteryData.census[key]
                if (typeof val === 'number') {
                    obj[key] = val // fmtd 在模板中处理
                } else if (val == null) {
                    obj[key] = 0
                } else {
                    obj[key] = val
                }
            })
        }
    } catch (error) {
        console.error(error)
    }
    return obj
})

const cellCount = computed(() => {
    try {
        return props.batteryData.bmu?.bmuList?.length || 0
    } catch (error) {
        return 0
    }
})

// Pack 数量计算
const packSize = computed(() => {
    try {
        return props.batteryData.bmu?.packSize || 0
    } catch (error) {
        return 0
    }
})

const isSingle = computed(() => {
    // 如果数量很少可能单列？暂时保留逻辑
    // if (packSize.value <= 1) {
    //     return true
    // } else {
    //     return packSize.value % 2 !== 0
    // }
    return false
})

const curPackIdx = ref(1)

const curBmu = computed(() => {
    const dft = { cells: [] }
    try {
        if (props.batteryData.bmu?.bumListGroup) {
            return props.batteryData.bmu.bumListGroup[curPackIdx.value - 1] ?? dft
        }
        return dft
    } catch (error) {
        console.error(error)
        return dft
    }
})

const getCellImage = (cell) => {
    try {
        const cells = curBmu.value.cells
        if (!cells || cells.length === 0) return cellImg

        let maxTemp = cells[0].T
        let minTemp = cells[0].T

        for (let i = 0; i < cells.length; i++) {
            const item = cells[i]
            if (item.T > maxTemp) {
                maxTemp = item.T
            }
            if (item.T < minTemp) {
                minTemp = item.T
            }
        }

        if (cell.T === maxTemp) {
            return cellHighImg
        }
        if (cell.T === minTemp) {
            return cellLowImg
        }
        return cellImg
    } catch (error) {
        console.error(error)
        return cellImg
    }
}
</script>

<style scoped lang="scss">
.cluster-detail-cell {
    display: flex;
    flex-direction: column;
    // height: 100%;
    gap: 16px;
}

.summary-row {
    display: flex;
    align-items: center;
    background: #fff;
    border-radius: 12px;
    padding: 20px;
    height: 106px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);

    .summary-item {
        height: 60px;
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: space-evenly;
        gap: 16px;

        &:first-child {
            flex: 0 1 180px;
        }

        .icon-box {
            display: flex;
            align-items: center;
            justify-content: center;

            img {
                object-fit: contain;
            }
        }

        .info {
            display: flex;
            flex-direction: column;
            gap: 4px;

            .label {
                font-size: 14px;
                color: #666;
            }

            .value {
                font-size: 18px;
                font-weight: 500;
                color: #000;
            }

            .value-group {
                display: flex;
                gap: 56px;
                font-size: 18px;
                font-weight: 500;
                color: #000;

                span {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                }
            }
        }
    }

    .divider {
        width: 1px;
        height: 40px;
        background: #E0E0E0;
    }
}

.main-content {
    flex: 1;
    display: flex;
    gap: 16px;
    min-height: 0;
}

.left-panel {
    width: 417px;
    max-height: 520px;
    background: #fff;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.pack-list-wrapper {
    max-height: 430px;
    overflow-y: auto;
    // flex: 1;
    // overflow-y: auto;
}

.pack-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    padding-right: 4px; // 滚动条空间

    &.single-col {
        grid-template-columns: 1fr;
    }

    .pack-item {
        position: relative;
        height: 76px; // 增加高度以适应图片
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s;

        // 使用背景图
        background-image: url('@/assets/images/station-monitor/detail-dialog/battery.png');
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;

        // 移除旧的边框和背景色
        border: none;
        background-color: transparent;

        .pack-num {
            font-size: 14px;
            color: #666;
            font-weight: 500;
            z-index: 1;
            margin-top: 2px; // 微调位置
        }

        &:hover {
            opacity: 0.8;
        }

        &.active {
            background-image: url('@/assets/images/station-monitor/detail-dialog/battery-select.png');

            .pack-num {
                color: #fff;
                font-weight: 600;
            }
        }
    }
}

.legend {
    display: flex;
    gap: 12px;

    .legend-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
        color: #666;

        .dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;

            &.normal {
                background: #33CC97;
            }

            &.low {
                background: #548CFC;
            }

            &.high {
                background: #FF7867;
            }
        }
    }
}

.right-panel {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.cell-grid {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(324px, 1fr));
    gap: 16px;
    align-content: start;
}

.cell-card {
    background: #fff;
    border: 1px solid #fff;
    border-radius: 16px;
    transition: all 0.2s;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    }
}

.cell-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 19px;
    border-radius: 16px 16px 0 0;
    background: linear-gradient(109deg, rgba(231, 237, 255, 0.40) 1.53%, rgba(255, 255, 255, 0.40) 98.28%);

    .cell-index {
        font-size: 14px;
        color: #333;
        font-weight: 500;
    }

    .pack-ver {
        font-size: 14px;
        color: #333;
        font-weight: 600;
    }
}

.cell-body {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;

    .cell-img {
        width: 76px;
        height: 50px;
        flex-shrink: 0;
        margin-right: 43px;

        img {
            width: 100%;
            height: 100%;
            object-fit: contain;
        }
    }

    .cell-info {
        flex: 1;
        display: flex;
        gap: 12px;
        flex-direction: column;
        justify-content: space-between;

        .info-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 14px;
            color: #2E2E2E;

            .label {
                font-weight: 300;
            }

            .value {
                font-size: 16px;
            }
        }
    }
}
</style>
